<template>
  <simple-edit-base
    ref="simpleEditBase"
    v-bind="$data"
    :filter-category="CMRVisibleCategories"
    :recommended-capacity="recommendedCapacity"
  >
    <template #header-subtitle>
      <div v-if="cmrUsedValue.overage > 0" class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
        <span class="opc-addon-config__subtitle-current">{{ $t('billing.cmr.current_usage') }}:</span>
        <span class="opc-addon-config__subtitle-used">{{ cmrUsedDesc.used }}</span>
        <span class="opc-addon-config__subtitle-overage">{{ cmrUsedDesc.overage }}</span>
        <zm-tooltip class="opc-addon-config-row__input__number--tips"
          disallow-learn-about
          :aria-label="`Learn more about ${cmrUsedValue.overage} GB Over capacity`" placement="bottom">
          <template #content>
            <div v-html="tips" />
          </template>
          <tips-icon type="info" :aria-label="`Learn more about ${cmrUsedValue.overage} GB Over capacity`" />
        </zm-tooltip>
      </div>
    </template>
    <template #header-description>
      <p v-if="desc">{{ desc }}</p>
    </template>
    <template #footer>
      <div class="opc-simple-edit-base__footer">
        <zm-banner v-if="willIncurOverage" class="opc-common-info-alert" type="warning" :show-close="false" show-icon>
          <template #title>
            <p>{{ $t('billing.cmr.option_incur_overage') }}</p>
          </template>
          <p v-html="willIncurOverage"></p>
        </zm-banner>
        <div class="opc-storage-select__footer-text">
          <p class="opc-addon-config__main__footer--tips">
              <span>*{{ $t("billing.cmr.overage_fees_desc") + " " + $t("billing.cmr.fees_vary_based_on_storage_plan") }}</span>
            <span v-for="(plan, index) in visiblePlanList" :key="index">
                {{ plan.cartCapacity }} {{ plan.cartCapacityUnit }}:
                {{ $t("billing.cmr.usage_in_excess", [plan.excessPrice]) }};
              </span>
            <a data-testid="checkout-a-45"
              href="https://support.zoom.us/hc/en-us/articles/360060661511-Cloud-recording-storage-capacity#h_01F4HFK8CMSEBNQZ5J624FHHW7"
              target="_blank">{{ $t("billing.common.Learn_more") }}</a>
          </p>
          <div class="opc-zm-base-card__btn--wrap">
            <zm-button data-testid="checkout-button-46" v-if="isShowRemoveBtn" type="link" ghost @click="removePlanByKey"
                      :aria-label="'remove ' + title + ' plan'">{{ $t("billing.common.remove") }}
            </zm-button>
          </div>
        </div>
        </div>
    </template>
  </simple-edit-base>
</template>

<script>
import SimpleEditBase from "@/components/simple-edit/base.vue";
import { mapGetters } from "vuex";
import { get } from "lodash";
import CMRCommon from "@/plans/CMR/mixin";
import { CMRVisibleCategories } from "@/plans/CMR/pre";
import { getMeetingDefaultCMRStorage, planTypes } from "@/common/constants";
import { handleRecommended, getExcessPriceCents, UNLIMITED_CMR } from "../constants";
import { translation } from "@/plugins/i18n";
import { transferMoney } from "@/utils/money";
import BillingMessageBox from "@/components/common/message-box";

export default {
  name: "CMREditSimple",
  mixins: [CMRCommon],
  components: {
    SimpleEditBase,
  },
  data() {
    return {
      showStorage: true, // Enable storage display
      showCycle: false,
      showLicenses: false,
      CMRVisibleCategories,
      mounted: false,
    };
  },
  computed: {
    ...mapGetters([
      "planMapFlat",
      "basicConfig",
      "cmrUsedValue",
      "getCurrentMeetingPlans",
    ]),
    cmrPlanList() {
      return this.planMapFlat.filter((p) => p.type === planTypes.CMR);
    },
    selectedPlan() {
      if (!this.mounted) return {};
      return this.$refs.simpleEditBase?.selectedPlan;
    },
    visiblePlanList() {
      if (!this.mounted) return [];
      return this.$refs.simpleEditBase?.selectablePlanList ?? [];
    },
    freeStorage() {
      return this.basicConfig?.freeStorage || "";
    },
    desc() {
      if (!!this.freeStorage && this.freeStorage !== UNLIMITED_CMR && this.freeStorage !== "0") {
        if (this.cmrUsedValue.overage > 0) {
          return translation("billing.cmr.add_desc");
        }
        return (
          translation("billing.cmr.account_cloud_storage_desc", [this.freeStorage]) +
          " " +
          translation("billing.cmr.add_desc")
        );
      } else if (this.freeStorage === UNLIMITED_CMR) {
        return "";
      } else {
        const totalCapacity = this.getCurrentMeetingPlans.reduce((sum, p) => {
          const licenseQuantity = get(p, "cartInfo.selected_number", 0);
          return sum + licenseQuantity * getMeetingDefaultCMRStorage(p, 0);
        }, 0);
        return this.$t("billing.cmr.config_desc", { totalCapacity });
      }
    },
    recommendedCapacity() {
      if (this.cmrUsedValue.overage > 0) {
        // find first value to meet this condition or the last one
        return handleRecommended(this.cmrPlanList, this.cmrUsedValue.overage);
      }
      return {};
    },
    willIncurOverage() {
      if (this.cmrUsedValue.overage > 0 && this.selectedPlan?.capacity < this.cmrUsedValue.overage) {
        const overage = this.cmrUsedValue.overage - this.selectedPlan.capacity;
        const money = transferMoney(getExcessPriceCents(this.selectedPlan.excessPriceCents || this.selectedPlan.excessPrice) * overage);
        return this.$t('billing.cmr.selection_will_incur_overage_desc', [`<b style="font-weight: 600">${money}</b>`]);
      }
      return '';
    },
    cmrUsedDesc() {
      const { used, total, overage, overageWithUnit } = this.cmrUsedValue;
      return {
        used: used ? `${used} GB of ${total} GB` : '',
        overage: overage ? `(${overageWithUnit} over capacity)` : ''
      }
    },
    tips() {
      return this.$t('billing.cmr.account_cloud_storage_include_desc', [`${this.cmrUsedValue.total} GB`]);
    },
  },
  mounted() {
    this.mounted = true;
    const selectedPlanCode = this.selectedPlan?.plan_code
    const freeUpgradeTargetPlan = this.cmrPlanList.find(p => p.old_plan_code === selectedPlanCode)
    if (freeUpgradeTargetPlan) {
      if (selectedPlanCode !== freeUpgradeTargetPlan.plan_code) {
        this.$refs.simpleEditBase?.handleStorageChange(freeUpgradeTargetPlan.capacity);
        // this.$tracking.addOnBuyInteraction(getTrackingProductNameByPlanType(planTypes.CMR), `${freeUpgradeTargetPlan?.cartCapacity}${freeUpgradeTargetPlan?.cartCapacityUnit}`);
        if (this.$query?.upgrade_now !== "true") {
          let newCapacity = `${freeUpgradeTargetPlan.cartCapacity}${freeUpgradeTargetPlan.cartCapacityUnit}`;
          BillingMessageBox.alert(
            this.$t("billing.cmr.cmr_free_upgrade.update_tip", [newCapacity]),
            this.$t("billing.cmr.cmr_free_upgrade.update_title"),
          );
        }
      }
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
@import '@/styles/scss-utils.scss';

.opc-common-info-alert.zbo-banner {
  background: var(--zoom-color-fill-subtler-warning, #FFF9F2);
  padding: 16px;
  margin-top: 16px;
}

.opc-zm-base-card__collapse {
  display: flex;
  align-items: center;

  &--reset {
    margin-top: 8px;
  }
}

.opc-addon-config__subtitle {
  &-current {
    color: var(--zoom-color-text-neutral, #686F79);
    font-weight: 500;
    font-size: 14px;
    line-height: 120%;
    margin-right: 2px;
    text-transform: initial;
  }

  &-used {
    color: var(--zoom-color-text-strong-neutral, #555B62);
    margin-left: 0;
    margin-right: 2px;
    font-weight: 500;
    font-size: 14px;
    line-height: 120%;
    text-transform: initial;
  }

  &-overage {
    color: var(--zoom-color-text-warning, #B36200);
    margin: 0;
    font-weight: 500;
    font-size: 14px;
    line-height: 120%;
    text-transform: initial;
  }
}

.opc-addon-config-row__input__number--tips {
  margin-left: 4px;
}

.opc-simple-edit-base__footer {
  margin-top: 16px;
}

.opc-storage-select__footer-text {
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--zoom-color-text-neutral, #686F79);
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}
</style>

